"use client";
import { I18nUtil } from "@utils/i18n";
import Translations from "@translate/features/onboarding-checklist/components/SetupChecklistSheet";

import React, { useEffect, useRef } from "react";
import { useSnapshot } from "valtio";
import { Icon } from "@ospweb/sdk-ui";
import { OspIcons } from "@ospweb/sdk-ui/src/Icon/OspIcons";
import { useIsPc } from "@hooks/useIsPc";
import { EdgenModalCreateAble } from "@/src/components/EdgenModal/index";
import {
  SetupChecklistStore,
  closeSheet,
  toggleExpandStep,
  fetchChecklist,
  refreshChecklistIfStale,
} from "@/src/store/modules/setupChecklist";
import type { ChecklistEntrySource, ChecklistStepKey } from "../types";
import { ChecklistStepAccordionItem } from "./ChecklistStepAccordionItem";
import { trackSheetView, trackSheetErrorView } from "../utils/tracking";
import { getOrderedChecklistSteps } from "../utils/checklistOrdering";
import { useSetupChecklistDispatcher } from "../hooks/useSetupChecklistDispatcher";

const I18n = I18nUtil.createScoped(Translations);

export interface SetupChecklistSheetContentProps {
  entrySource?: ChecklistEntrySource;
  onClose?: () => void;
  /**
   * 动作派发时是否先关闭承载面。modal 场景（默认 true）先收弹层再跳转；
   * 独立页面（/setup-checklist，供原生 WebView 承载）传 false —— 跳转由路由
   * 自行离场（app 内 closeDetailInNative 会直接拆掉 WebView，先 close 会把
   * 后续 dispatch 的跳转一起吞掉）。
   */
  closeOnAction?: boolean;
}

/**
 * S1.4: 起步清单全屏页面 — Figma "Setup checklist"（#105389 PC / #104392 移动）
 * 白圆返回钮 Header + Reward 条 + 步骤折叠行列表 + 错误重试态
 */
export const SetupChecklistSheetContent: React.FC<
  SetupChecklistSheetContentProps
> = ({ entrySource = "sheet", onClose, closeOnAction = true }) => {
  const checklistSnap = useSnapshot(SetupChecklistStore);
  const data = checklistSnap.data;
  const { dispatch } = useSetupChecklistDispatcher();
  const [isPc] = useIsPc();

  const completedCount = data?.completedCount ?? 0;
  const activeStepId = checklistSnap.activeExpandedStepId;
  // 已完成置顶、未完成沉底（各组内保持 stepMeta 固定顺序）
  const orderedSteps = getOrderedChecklistSteps(data?.steps);

  // 曝光只在打开后的首个确定态（拿到数据或读取失败）上报一次，不随数据刷新、失败重试重复上报
  const sheetViewTrackedRef = useRef(false);
  useEffect(() => {
    if (sheetViewTrackedRef.current || (!data && !checklistSnap.error)) {
      return;
    }
    sheetViewTrackedRef.current = true;
    trackSheetView(entrySource, completedCount);
  }, [entrySource, completedCount, data, checklistSnap.error]);

  // 只在「无数据、没在请求、也没失败」时补拉：失败后不自动重试（由 Retry 按钮触发），避免接口故障时无限请求
  useEffect(() => {
    if (!data && !checklistSnap.loading && !checklistSnap.error) {
      void fetchChecklist();
    }
  }, [data, checklistSnap.loading, checklistSnap.error]);

  // 每次打开清单都按节流刷新一次，避免展示打开前的陈旧状态
  useEffect(() => {
    void refreshChecklistIfStale();
  }, []);

  useEffect(() => {
    if (checklistSnap.error) {
      trackSheetErrorView("Failed to fetch checklist status");
    }
  }, [checklistSnap.error]);

  const handleClose = () => {
    closeSheet();
    onClose?.();
  };

  const handleAction = (stepKey: ChecklistStepKey) => {
    closeSheet();
    if (closeOnAction) {
      onClose?.();
    }
    dispatch(stepKey);
  };

  return (
    <div
      data-testid="setup-checklist-sheet-content"
      data-openreplay-label="sheet_setup_checklist"
      className="flex h-[100dvh] w-screen flex-col overflow-hidden bg-background_solid_secondary"
    >
      {/* Header：白圆返回钮 + 页面标题（PC 内容列与 Body 同轴 740px 居中） */}
      <div className="shrink-0 px-4 py-2">
        <div className="mx-auto flex w-full max-w-[740px] items-center gap-3">
          <button
            type="button"
            onClick={handleClose}
            data-openreplay-label="checklist_back"
            className="flex size-10 shrink-0 items-center justify-center rounded-full bg-white shadow-[0px_2px_20px_0px_rgba(0,0,0,0.08)]"
            aria-label={I18n.t("Back")}
          >
            {/* PC=left-arrow-alt（#105389）；移动=close ×（#104392 Header back+title 用 close 图标） */}
            <Icon
              name={isPc ? "left-arrow-alt" : "close"}
              size={20}
              className="text-content_primary"
            />
          </button>
          <h2 className="title_xl text-content_primary">
            {I18n.t("Setup checklist with Ed")}
          </h2>
        </div>
      </div>

      {/* 错误态与重试 */}
      {checklistSnap.error && !data ? (
        <div className="flex flex-1 flex-col items-center justify-center gap-4 px-4 text-center">
          <p className="body_m text-content_tertiary">
            {I18n.t("Unable to sync latest setup progress.")}
          </p>
          <button
            type="button"
            onClick={() => fetchChecklist(true)}
            data-openreplay-label="checklist_retry"
            data-or-event="onboard_sheet_retry_tap"
            data-or-param-entry={entrySource}
            className="inline-flex h-11 items-center justify-center rounded-lg bg-background_brand_overlay100 px-4 text-white button_m"
          >
            {I18n.t("Retry")}
          </button>
        </div>
      ) : (
        <div className="flex-1 overflow-y-auto">
          {/* 移动 Steps 容器 padding-top 16（#104392）；PC Body 顶部 padding 0（#105389） */}
          <div className="mx-auto w-full max-w-[740px] px-4 pb-8 pt-4 md:pt-0">
            <div className="flex flex-col gap-3">
              {/* Reward 条（Figma Strip/Reward #109640:104395）：浅底条用深青版雷达（Coverage/Radar #109640:104396），
                  coverage-radar 是深色卡片用的荧光黄版，不能混用 */}
              <div className="flex items-center gap-4 rounded-lg bg-background_brand_overlay10 p-3">
                <OspIcons
                  name="coverage-radar-light"
                  size={48}
                  className="shrink-0"
                  aria-hidden="true"
                />
                <div className="flex flex-col gap-0.5">
                  <p className="title_m text-content_brand_primary">
                    {I18n.t(
                      "The more Ed knows you, the better Ed works for you",
                    )}
                  </p>
                  <p className="body_s max-w-[270px] text-content_tertiary">
                    {I18n.t(
                      "Finish these and Ed will know you well enough to do all of it better.",
                    )}
                  </p>
                </div>
              </div>

              {/* 任务列表：6 项全量展示（notification 仅 app 端可完成，PC 展示等待同步）；
                  已完成置顶、未完成沉底（各组内保持固定顺序） */}
              {orderedSteps.map(({ meta, stepData }) => (
                <ChecklistStepAccordionItem
                  key={meta.stepId}
                  stepData={stepData}
                  meta={meta}
                  isExpanded={activeStepId === meta.stepId}
                  onToggle={() => toggleExpandStep(meta.stepId)}
                  onAction={handleAction}
                  entrySource={entrySource}
                />
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

/**
 * 命令式呼出起步清单全屏页面浮层
 * PC / 移动均为全屏接管（参照 AlphaPickPricingModalCallable 全屏模式）
 */
export const openSetupChecklistSheetModal = (
  entrySource: ChecklistEntrySource = "topleft",
): Promise<boolean> => {
  return new Promise((resolve) => {
    // react-call 单参 end(response) 会 end 全部 active calls，且 300ms 后的清理会把
    // 整个 stack 清空 —— 清单关闭后 dispatch 立刻打开的下一个 EdgenModal（Plaid 连接
    // Flow / 建 Tracker 分类弹窗）会被连带拆除（症状：brokers 请求已发、弹窗随即消失）。
    // 必须用双参 end(promise, response) 只结束本弹窗（同 openPlaidConnectFlowModal 的 finish）。
    const modalPromise: Promise<boolean> = EdgenModalCreateAble.call({
      children: (
        <SetupChecklistSheetContent
          entrySource={entrySource}
          onClose={() => {
            EdgenModalCreateAble.end(modalPromise, false);
            resolve(false);
          }}
        />
      ),
      onCancel: () => {
        closeSheet();
        EdgenModalCreateAble.end(modalPromise, false);
        resolve(false);
      },
      destroyOnClose: true,
      // OspUI 根样式把 .ant-modal 钉死为 width: fit-content !important（packages/osp-ui/src/modal/styles.ts），
      // 全屏页参照 openPlaidConnectFlowModal 的壳层方案：dialog 层去 margin/max-width/padding，
      // 由内容根节点用 w-screen + 100dvh 的绝对尺寸把 fit-content 撑满整个视口。
      className: "!m-0 !max-w-none !p-0",
      containerClassName: "bg-background_solid_secondary",
      contentStyle: {
        borderRadius: 0,
        width: "fit-content",
        maxWidth: "100vw",
        height: "auto",
        maxHeight: "100dvh",
        padding: 0,
        overflow: "hidden",
      },
      containerStyle: {
        width: "fit-content",
        height: "fit-content",
        maxWidth: "100vw",
        maxHeight: "100dvh",
        overflow: "hidden",
      },
      showClose: false,
      styles: {
        wrapper: {
          zIndex: 10000,
        },
        mask: {
          zIndex: 10000,
        },
      },
    });
  });
};
